import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../config/firebase";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import "./orderDetails.css"; // Import CSS for styling

const OrderDetailsRoute = () => {
    const { userId, orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");
    const [updating, setUpdating] = useState(false);
    const [imageErrors, setImageErrors] = useState({});
    const statusOptions = ["Pending", "Confirmed", "Rejected"];

    useEffect(() => {
        const fetchOrderAndUser = async () => {
            console.log("Fetching order with userId:", userId, "and orderId:", orderId);
            try {
                setLoading(true);

                // Fetch order data
                const orderRef = doc(db, `users/${userId}/orders`, orderId);
                console.log("Order reference path:", orderRef.path);
                const orderSnap = await getDoc(orderRef);

                // Fetch user data
                const userRef = doc(db, "users", userId);
                const userSnap = await getDoc(userRef);

                if (orderSnap.exists()) {
                    let orderData = { id: orderSnap.id, ...orderSnap.data(), userId };
                    console.log("Order data fetched:", orderData);
                    if (orderData.createdAt instanceof Timestamp) {
                        orderData.createdAt = orderData.createdAt.toDate();
                    }
                    setOrder(orderData);
                    setStatus(orderData.orderStatus || "Pending");
                } else {
                    console.log("Order document does not exist at path:", orderRef.path);
                    setError("Order not found.");
                }

                if (userSnap.exists()) {
                    setUserData(userSnap.data());
                }
            } catch (error) {
                console.error("Error fetching data:", error.message, error.stack);
                setError("Failed to fetch data: " + error.message);
            } finally {
                setLoading(false);
                console.log("Loading state set to false, current state:", { loading: false, order, error });
            }
        };

        fetchOrderAndUser();
    }, [userId, orderId]);

    const handleClose = () => {
        navigate("/orders");
    };

    const handleStatusChange = (e) => {
        setStatus(e.target.value);
    };

    const handleUpdateOrder = async () => {
        if (!order) return;

        try {
            setUpdating(true);
            const orderRef = doc(db, `users/${userId}/orders`, orderId);
            await updateDoc(orderRef, { orderStatus: status });
            const updatedOrder = { ...order, orderStatus: status };
            setOrder(updatedOrder);
            console.log("Order updated in Firestore:", updatedOrder);
            alert("Order status updated successfully!");
        } catch (error) {
            console.error("Error updating order status:", error.message, error.stack);
            setError("Failed to update order status: " + error.message);
            setStatus(order.orderStatus);
        } finally {
            setUpdating(false);
        }
    };

    const formatPrice = (amount) => {
        if (!amount) return "₹ 0.00";
        if (amount.startsWith("₹ ") && /^\₹\s\d+(\.\d{2})?$/.test(amount)) {
            return amount;
        }
        const numericValue = parseFloat(amount.replace(/[^0-9.]/g, ""));
        return isNaN(numericValue) ? "₹ 0.00" : `₹ ${numericValue.toFixed(2)}`;
    };

    const handleImageError = (index) => {
        setImageErrors(prev => ({ ...prev, [index]: true }));
    };

    if (loading) {
        return <p>Loading order details...</p>;
    }

    if (error) {
        return <p className="error">{error}</p>;
    }

    if (!order) {
        return <p>No order data available.</p>;
    }

    return (
        <div className="order-details-container">
            {/* Fixed Header */}
            <div className="order-header">
                <h2>Order Details</h2>
                <p><strong>Order ID:</strong> {order.id}</p>
                <p><strong>Placed on:</strong> {order.orderDetails?.orderDate || "N/A"}</p>
                <p><strong>Total Amount:</strong> ₹ {order.totals?.total?.toFixed(2) || "0.00"}</p>
                <p><strong>Item Count:</strong> {order.items?.length || 0} Items</p>

                <div className="form-group">
                    <label><strong>Status:</strong></label>
                    <select
                        value={status}
                        onChange={handleStatusChange}
                        className="status-select"
                        disabled={updating}
                    >
                        {statusOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    <button
                        onClick={handleUpdateOrder}
                        className="update-btn"
                        disabled={updating}
                    >
                        {updating ? "Updating..." : "Update"}
                    </button>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="order-scrollable-content">
                <h3>Customer Details</h3>
                <div className="address-details">
                    {userData && (
                        <>
                            <p><strong>Name:</strong> {userData.name || "N/A"}</p>
                            <p><strong>Phone:</strong> {userData.phoneNumber || "N/A"}</p>
                            {userData.address && <p><strong>Address:</strong> {userData.address}</p>}
                            {userData.imageUrl && (
                                <div className="user-image">
                                    <img
                                        src={userData.imageUrl}
                                        alt="User"
                                        className="user-img"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://via.placeholder.com/100?text=No+Image";
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                <h3>Delivery Address</h3>
                {order.deliveryAddress ? (
                    <div className="address-details">
                        <p><strong>Address:</strong> {order.deliveryAddress.address || "N/A"}</p>
                        <p><strong>Area:</strong> {order.deliveryAddress.area || "N/A"}</p>
                        <p><strong>Landmark:</strong> {order.deliveryAddress.landmark || "N/A"}</p>
                        <p><strong>Pincode:</strong> {order.deliveryAddress.pincode || "N/A"}</p>
                        {order.deliveryAddress.alternatePhone && (
                            <p><strong>Alternate Phone:</strong> {order.deliveryAddress.alternatePhone || "N/A"}</p>
                        )}

                    </div>
                ) : (
                    <p>No delivery address available.</p>
                )}

                <h3>Items Ordered</h3>
                <ul className="items-list">
                    {order.items?.length > 0 ? (
                        order.items.map((item, index) => (
                            <li key={index} className="item">
                                <div className="item-img-container">
                                    <img
                                        src={imageErrors[index] ? "https://via.placeholder.com/100?text=No+Image" : item.cartFoodImage}
                                        alt={item.cartFoodName}
                                        className="item-img"
                                        onError={() => handleImageError(index)}
                                    />
                                </div>
                                <div className="item-details">
                                    <p className="item-name">{item.cartFoodName}</p>
                                    <p className="item-price">{formatPrice(item.cartFoodAmount)}</p>
                                    <p className="item-quantity">Quantity: {item.cartQuantity || item.cartFoodQuantity}</p>
                                </div>
                            </li>
                        ))
                    ) : (
                        <p>No items found.</p>
                    )}
                </ul>
            </div>

            {/* Fixed Footer */}
            <div className="order-footer">
                <button onClick={() => window.history.back()} className="close-btn">
                    Close
                </button>
            </div>
        </div>
    );
};

export default OrderDetailsRoute;